import {
  useAccount,
  useContractRead,
  useContractWrite,
  useWaitForTransaction,
} from "wagmi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectAmount, selectBuyMode, setAmount } from "../features/trade";
import { Chain, Chains, SpaceSellABI, WBTCBalanceABI, WISHGetPrice } from "./data";
import { selectChain } from "../features/chain";
import { selectToken } from "../features/token";
import { parseAmount, specifyGasLimit } from "../utils/util";
import toast from "react-hot-toast";
import BigNumber from "bignumber.js";
import { ethers } from "ethers";
import { fetchPrice, getMinSell } from "../app/store";
import { useTrade } from "./accountContext";
import { useEffect, useState } from "react";
import { getJicAddress, onJicChange } from "../lib/jic";
import {
  sendInjectedContractTransaction,
  waitForInjectedReceipt,
} from "../lib/injectedWallet";

const SellButton = () => {
  const priceScale = new BigNumber(2).pow(64);
  const sellFeeMultiplier = new BigNumber("0.92");
  const buyMode = useAppSelector(selectBuyMode);
  const chain = useAppSelector(selectChain);
  const token = useAppSelector(selectToken);
  const amount = useAppSelector(selectAmount);
  const { address: signerAddress } = useAccount();
  const [jicAddress, setJicAddress] = useState<string | null>(() =>
    getJicAddress()
  );
  const accountAddress = signerAddress || jicAddress;
  const { reFetchBalance } = useTrade();
  const dispatch = useAppDispatch();
  const [injectedPending, setInjectedPending] = useState(false);

  useEffect(
    () =>
      onJicChange((user) => {
        setJicAddress(user?.sub ? user.sub.toLowerCase() : null);
      }),
    []
  );

  const {
    data: sellRes,
    isLoading: sellLoading,
    write: sell,
    reset: resetSell,
  } = useContractWrite({
    chainId: Chains[chain].chainId,
    address: Chains[chain].contractAddr as `0x${string}`,
    abi: SpaceSellABI,
    functionName: "Sell",
    args: [Chains[chain].wbtcAddr[token].addr, parseAmount(amount, 18)],
    gas: BigInt(specifyGasLimit(chain)),
  });
  const { isLoading: waitingEvmSell } = useWaitForTransaction({
    hash: sellRes?.hash,
    onSuccess() {
      toast.success("Sell succeeded!");
      reFetchBalance();
      dispatch(setAmount(""));
      resetSell();
    },
    onError() {
      toast.error("Sell failed!");
      reFetchBalance();
      dispatch(setAmount(""));
      resetSell();
    },
  });
  const { refetch: refetchPoolUSDBalance } = useContractRead({
    chainId: Chains[chain].chainId,
    address: Chains[chain].wbtcAddr[token].addr as `0x${string}`,
    abi: WBTCBalanceABI,
    functionName: "balanceOf",
    args: [Chains[chain].contractAddr],
    enabled: false,
  });
  const { refetch: refetchVaultPrice } = useContractRead({
    chainId: Chains[chain].chainId,
    address: Chains[chain].contractAddr as `0x${string}`,
    abi: WISHGetPrice,
    functionName: "getPrice",
    enabled: false,
  });

  const readSellPrice = async () => {
    const { data } = await refetchVaultPrice().catch(() => ({ data: undefined }));
    if (data !== undefined && data !== null) {
      return new BigNumber(data.toString()).div(priceScale);
    }
    return new BigNumber(fetchPrice());
  };

  const sendInjectedSellTransaction = async () => {
    const txHash = await sendInjectedContractTransaction({
      chainId: Chains[chain].chainId,
        chainName: Chains[chain].chainName,
        rpcUrl: Chains[chain].provider,
      to: Chains[chain].contractAddr as `0x${string}`,
      abi: SpaceSellABI,
      functionName: "Sell",
      args: [Chains[chain].wbtcAddr[token].addr, parseAmount(amount, 18)],
      gas: BigInt(specifyGasLimit(chain)),
    });
    await waitForInjectedReceipt(txHash, {
        rpcUrl: Chains[chain].provider,
      });
  };

  async function sellToken() {
    if (!accountAddress) {
      toast.error("Please connect wallet first");
      return;
    }
    if (amount === "" || !Number.isFinite(Number(amount)) || Number(amount) < getMinSell()) {
      toast.error("Please enter a valid amount");
      return;
    }
    const estimatedUsd = new BigNumber(amount)
      .times(await readSellPrice())
      .times(sellFeeMultiplier);

    try {
      if (!signerAddress) setInjectedPending(true);
      const { data } = await refetchPoolUSDBalance().catch(() => ({
        data: undefined,
      }));
      if (data !== undefined && data !== null) {
        const poolBalance = data as BigNumber;
        const poolBalanceStr = ethers.formatUnits(
          poolBalance.toString(),
          Chains[chain].wbtcAddr[token].decimal
        );
        if (estimatedUsd.toNumber() > parseFloat(poolBalanceStr)) {
          toast.error(
            "insufficient usd in contract, current balance:" + poolBalanceStr
          );
          return;
        }
      }

      if (signerAddress) {
        sell?.();
        return;
      }

      await sendInjectedSellTransaction();
      toast.success("Sell succeeded!");
      reFetchBalance();
      dispatch(setAmount(""));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Sell failed");
    } finally {
      setInjectedPending(false);
    }
  }

  return (
    <button
      className=" p-1 mt-1 rounded-full h-8 md:h-9 text-sm md:text-base text-white  w-20 md:w-28"
      style={{
        background: "linear-gradient(120deg,#b7b7b7 36.9%,#363636 88.84%)",
        opacity: buyMode ? "0.4" : "1",
      }}
      disabled={
        buyMode ||
        sellLoading ||
        waitingEvmSell ||
        injectedPending ||
        amount === "" ||
        Number(amount) < getMinSell()
      }
      onClick={() => {
        sellToken();
      }}
    >
      {sellLoading || waitingEvmSell || injectedPending ? "Processing" : "SELL"}
    </button>
  );
};

export default SellButton;
